// frontend/src/pages/reports/ClassMarkReportPage.js
import React, { useState, useEffect } from 'react';
import {
  Table, Card, Space, Button, Select, Spin, Typography, Row, Col,
  Empty, Tag, message
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { getAllClasses } from '../../services/classService';
import { getAllExams } from '../../services/examService';
import { getClassReport } from '../../services/markService';
import { getClassSubjects } from '../../services/assignmentService';
import { useAcademicYear } from '../../context/AcademicYearContext';
import { jsPDF } from 'jspdf'; // Import jsPDF
import autoTable from 'jspdf-autotable'; // Import jspdf-autotable
import dayjs from 'dayjs'; // Import dayjs for date formatting

const { Title, Text } = Typography;

const ClassMarkReportPage = () => {
  const { currentYear } = useAcademicYear();
  const [classes, setClasses] = useState([]);
  const [exams, setExams] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedExam, setSelectedExam] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [classSubjectIds, setClassSubjectIds] = useState([]);
  const [pageSize, setPageSize] = useState(20); // Add pageSize state

  // Helper to ensure all values are strings for PDF
  const ensureString = (value) => {
    if (value === null || value === undefined) return '';
    return String(value);
  };

  const formatScore = (value) => {
    const num = parseFloat(value);
    if (isNaN(num) || value === null || value === undefined) return '-';
    if (num % 1 === 0) {
      return num.toFixed(0);
    }
    return num.toFixed(2);
  };

  // Helper to get color based on score percentage
  const getScoreColor = (score, totalMarks) => {
    if (score === null || score === undefined || totalMarks === null || totalMarks === undefined) {
      return { fillColor: '#ffffff', textColor: '#000000' };
    }
    const percentage = (parseFloat(score) / parseFloat(totalMarks)) * 100;
    if (percentage >= 75) return { fillColor: '#d4edda', textColor: '#000000' }; // Green
    else if (percentage >= 40) return { fillColor: '#fff3cd', textColor: '#000000' }; // Yellow
    else return { fillColor: '#f8d7da', textColor: '#000000' }; // Red
  };

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [classesData, examsData] = await Promise.all([
          getAllClasses(),
          getAllExams()
        ]);
        setClasses(classesData);
        setExams(examsData);
        setClasses(classesData);
        setExams(examsData);
      } catch (error) {
        message.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [currentYear]);

  // Fetch class marks when filters change
  useEffect(() => {
    if (!selectedClass || !selectedExam) {
      setReportData([]);
      setSubjects([]);
      return;
    }

    // Also load class level subjects and keep them in state
    // (async () => {
    //   if (!selectedClass) {
    //     setClassSubjectIds([]);
    //   } else {
    //     try {
    //       const ids = await getClassSubjects(selectedClass);
    //       setClassSubjectIds(ids);
    //     } catch (err) {
    //       setClassSubjectIds([]);
    //     }
    //   }
    // })();

    const fetchMarks = async () => {
      setLoading(true);
      try {
        const response = await getClassReport(selectedClass, selectedExam);

        if (response.subjects) {
          setSubjects(response.subjects);
          setReportData(response.students || []);
        } else {
          // Fallback for backward compatibility or error
          setSubjects([]);
          setReportData([]);
        }
      } catch (error) {
        message.error('Failed to fetch marks');
        console.error('ClassMarkReportPage - Error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMarks();
  }, [selectedClass, selectedExam]);

  // Load class-level subject ids separately (so we can include configured-only subjects even if marks are empty)
  useEffect(() => {
    const loadClassSubjects = async () => {
      if (!selectedClass) {
        setClassSubjectIds([]);
        return;
      }
      try {
        const ids = await getClassSubjects(selectedClass);
        setClassSubjectIds(ids);
      } catch (err) {
        console.error('Failed to fetch class subjects', err);
        setClassSubjectIds([]);
      }
    };
    loadClassSubjects();
  }, [selectedClass]);

  // Generate dynamic columns based on subjects
  // Compute a map of subject id -> total marks (highest seen in fetched marks)
  const subjectTotals = subjects.reduce((acc, s) => ({ ...acc, [s.id]: null }), {});
  reportData.forEach(student => {
    (student.subjects || []).forEach(sub => {
      const id = sub.subject_id;
      const tm = sub.total_marks ? parseFloat(sub.total_marks) : null;
      if (tm !== null) {
        if (!subjectTotals[id] || tm > subjectTotals[id]) subjectTotals[id] = tm;
      }
    });
  });
  const generateColumns = () => {
    const baseColumns = [
      {
        title: 'Roll No',
        dataIndex: 'roll_no',
        key: 'roll_no',
        width: 80,
        fixed: 'left',
        align: 'center',
      },
      // {
      //   title: 'Admission Number',
      //   dataIndex: 'admission_no',
      //   key: 'admission_no',
      //   width: 140,
      //   fixed: 'left',
      //   align: 'center',
      // },
      {
        title: 'Name',
        dataIndex: 'student_name',
        key: 'student_name',
        width: 200,
        fixed: 'left',
      },
    ];

    // Add subject columns dynamically with (mark/total) format
    const subjectColumns = subjects.map(subject => ({
      title: `${subject.name} (${subjectTotals[subject.id] ? subjectTotals[subject.id] : '-'})`,
      key: `subject_${subject.id}`,
      width: 120,
      align: 'center',
      render: (_, record) => {
        const subjectData = record.subjects?.find(s => s.subject_id === subject.id);
        if (!subjectData || subjectData.score === null) {
          return <Text type="secondary">-</Text>;
        }
        const score = parseFloat(subjectData.score);
        const totalMarks = subjectTotals[subject.id] || (subjectData.total_marks ? parseFloat(subjectData.total_marks) : null);
        const percentage = totalMarks ? (score / totalMarks) * 100 : null;

        let color = 'default';
        if (percentage !== null) {
          if (percentage >= 75) color = 'success';
          else if (percentage >= 40) color = 'warning';
          else color = 'error';
        }

        return (
          <Tag color={color}>
            {score}
          </Tag>
        );
      }
    }));

    // Add total marks column (obtained/total)
    const totalColumn = {
      title: 'Total Marks',
      key: 'total',
      width: 130,
      align: 'center',
      render: (_, record) => {
        const totalScore = subjects.reduce((sum, subj) => {
          const sd = record.subjects?.find(s => s.subject_id === subj.id);
          return sum + (parseFloat(sd?.score) || 0);
        }, 0);
        const totalMarks = subjects.reduce((sum, subj) => {
          const t = subjectTotals[subj.id] || 0;
          return sum + t;
        }, 0);

        return (
          <strong>
            {totalScore?.toFixed(0) || 0}/{totalMarks?.toFixed(0) || 0}
          </strong>
        );
      }
    };

    // Add percentage column
    const percentageColumn = {
      title: 'Percentage',
      key: 'percentage',
      width: 110,
      align: 'center',
      render: (_, record) => {
        // Percentage should be calculated using header subjects and their canonical totals
        const totalScore = subjects.reduce((sum, subj) => {
          const sd = record.subjects?.find(s => s.subject_id === subj.id);
          return sum + (parseFloat(sd?.score) || 0);
        }, 0);
        const totalMarks = subjects.reduce((sum, subj) => sum + (subjectTotals[subj.id] || 0), 0);

        if (!totalMarks) return '-';

        const percentage = (totalScore / totalMarks) * 100;
        const color = percentage >= 75 ? 'success' : percentage >= 40 ? 'warning' : 'error';

        return (
          <Tag color={color}>
            {percentage.toFixed(2)}%
          </Tag>
        );
      }
    };

    return [...baseColumns, ...subjectColumns, totalColumn, percentageColumn];
  };

  const handleExport = () => {
    if (reportData.length === 0) {
      message.warning('No data to export');
      return;
    }

    const classData = classes.find(c => c.id === selectedClass);
    const examData = exams.find(e => e.id === selectedExam);

    // Create CSV header
    const csvRows = [
      [`Class Mark Sheet - ${classData?.name || 'N/A'}`],
      [`Exam: ${examData?.name || 'N/A'}`],
      [''],
      ['Roll No', 'Name', ...subjects.map(s => `${s.name} (${subjectTotals[s.id] ? subjectTotals[s.id] : '-'})`), 'Total Marks', 'Percentage'].join(',')
    ];

    // Add student data
    reportData.forEach(student => {
      const scores = subjects.map(subject => {
        const subjectData = student.subjects?.find(s => s.subject_id === subject.id);
        if (subjectData && subjectData.score !== null) {
          return `${subjectData.score}`;
        }
        return '-';
      });

      const totalScore = subjects.reduce((sum, subj) => {
        const sd = student.subjects?.find(s => s.subject_id === subj.id);
        return sum + (parseFloat(sd?.score) || 0);
      }, 0);
      const totalMarks = subjects.reduce((sum, subj) => sum + (subjectTotals[subj.id] || 0), 0);
      const percentage = totalMarks ? ((totalScore / totalMarks) * 100).toFixed(2) + '%' : '-';
      const totalFormatted = `${totalScore?.toFixed(0) || 0}/${totalMarks?.toFixed(0) || 0}`;

      csvRows.push(
        // Corrected: Use student.roll_no for the first column
        [student.roll_no, student.student_name, ...scores, totalFormatted, percentage].join(',')
      );
    });

    const csv = csvRows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `class_marks_${classData?.name || 'report'}_${examData?.name || 'exam'}.csv`;
    a.click();
  };

  const handleDownloadPDF = () => {
    if (reportData.length === 0) {
      message.warning('No data to download');
      return;
    }

    const classData = classes.find(c => c.id === selectedClass);
    const examData = exams.find(e => e.id === selectedExam);

    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 15;
      let yPos = margin;

      // ===== 1. REPORT HEADER =====
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.text('KALLADI ABDU HAJI HIGHER SECONDARY SCHOOL', pageWidth / 2, yPos, { align: 'center' });
      yPos += 8;

      doc.setFontSize(12);
      doc.setFont(undefined, 'normal');
      doc.text('Class Mark Report', pageWidth / 2, yPos, { align: 'center' });
      yPos += 5;

      doc.setFontSize(9);
      doc.setTextColor(150);
      doc.text(`Report Generated: ${dayjs().format('DD-MM-YYYY')}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 12;
      doc.setTextColor(0);

      // ===== 2. CLASS & EXAM DETAILS =====
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text(`Class: ${classData?.name || 'N/A'}`, margin, yPos);
      yPos += 5;
      doc.text(`Exam: ${examData?.name || 'N/A'}`, margin, yPos);
      yPos += 10;

      // ===== 3. MARKS TABLE =====
      const head = [
        [
          'Roll No',
          'Name',
          ...subjects.map(s => `${s.name} (${subjectTotals[s.id] ? subjectTotals[s.id] : '-'})`),
          'Total Marks',
          'Percentage'
        ]
      ];

      const body = reportData.map(student => {
        const row = [];

        // Roll No, Admission No, Name
        row.push({
          content: ensureString(student.roll_no),
          styles: { halign: 'center' }
        });
        // Omit admission number from export PDF header — only show Roll No and Name
        row.push({
          content: ensureString(student.student_name),
          styles: { halign: 'left' }
        });

        // Student total should sum scores for the header-listed subjects. Student max should be sum of header subject totals
        let studentTotalScore = 0;
        let studentTotalMax = 0;

        subjects.forEach(subject => {
          const subjectData = student.subjects?.find(s => s.subject_id === subject.id);
          if (subjectData && subjectData.score !== null) {
            const score = parseFloat(subjectData.score);
            const totalMarks = parseFloat(subjectData.total_marks);
            studentTotalScore += score || 0;
            // Use the canonical subject total found in subjectTotals if available, else fallback to totalMarks
            studentTotalMax += subjectTotals[subject.id] || totalMarks || 0;
            const colors = getScoreColor(score, totalMarks);
            row.push({
              content: `${formatScore(score)}`,
              styles: { halign: 'center', ...colors }
            });
          } else {
            row.push({
              content: '- ',
              styles: { halign: 'center' }
            });
          }
        });

        // Total Marks with color
        const totalScoreFormatted = formatScore(studentTotalScore);
        const totalMarksFormatted = formatScore(studentTotalMax);
        const totalColors = getScoreColor(studentTotalScore, studentTotalMax);
        row.push({
          content: `${totalScoreFormatted}/${totalMarksFormatted}`,
          styles: { fontStyle: 'bold', halign: 'center', ...totalColors }
        });

        // Percentage with color
        const percentage = studentTotalMax > 0 ? ((studentTotalScore / studentTotalMax) * 100).toFixed(2) : '-';
        const percentageNum = percentage === '-' ? 0 : parseFloat(percentage);
        const percentageColors = percentage === '-' ?
          { fillColor: '#ffffff', textColor: '#000000' } :
          getScoreColor(percentageNum, 100);
        row.push({
          content: percentage === '-' ? '-' : `${percentage}%`,
          styles: { fontStyle: 'bold', halign: 'center', ...percentageColors }
        });

        return row;
      });

      autoTable(doc, {
        head: head,
        body: body,
        startY: yPos,
        theme: 'grid',
        headStyles: { fillColor: '#333', textColor: '#fff', fontStyle: 'bold', halign: 'center' },
        styles: { fontSize: 8, cellPadding: 1.5 },
        columnStyles: {
          0: { halign: 'center' }, // Roll No
          1: { halign: 'left' }    // Name
        },
        didDrawPage: (data) => {
          yPos = data.cursor.y;
        }
      });

      // ===== 4. SAVE DOCUMENT =====
      const fileName = `class_marks_${classData?.name || 'report'}_${examData?.name || 'exam'}_${dayjs().format('YYYY-MM-DD')}.pdf`;
      doc.save(fileName);
      message.success('Class mark report downloaded successfully!');

    } catch (error) {
      console.error('PDF generation error:', error);
      message.error('Failed to generate PDF report.');
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div>
          <Title level={2}>📊 Class-wise Marks Report</Title>
          <Text type="secondary">
            View all subjects marks for an entire class in a single view
          </Text>
        </div>

        {/* Filters */}
        <Card>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={8}>
              <label>Select Class *</label>
              <Select
                placeholder="Choose a class"
                value={selectedClass}
                onChange={setSelectedClass}
                loading={loading}
                options={classes.map(c => ({
                  label: c.name,
                  value: c.id,
                }))}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} sm={8}>
              <label>Select Exam *</label>
              <Select
                placeholder="Choose an exam"
                value={selectedExam}
                onChange={setSelectedExam}
                loading={loading}
                options={exams.map(e => ({
                  label: e.name,
                  value: e.id,
                }))}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} sm={8}>
              <label>Class Subjects</label>
              <div style={{ paddingTop: 6 }}>
                {subjects && subjects.length > 0 ? (
                  <div>
                    {subjects.map(s => s.name).join(', ')}
                  </div>
                ) : (
                  <div style={{ color: '#888' }}>No subjects assigned for this class</div>
                )}
              </div>
            </Col>
            <Col xs={24} sm={8}>
              <label>Rows per page</label>
              <Select
                value={pageSize}
                onChange={setPageSize}
                options={[
                  { label: '10', value: 10 },
                  { label: '20', value: 20 },
                  { label: '50', value: 50 },
                  { label: '100', value: 100 }
                ]}
                style={{ width: '100%' }}
              />
            </Col>
          </Row>
        </Card>

        {/* Results */}
        {selectedClass && selectedExam ? (
          <Card
            title={`Marks for ${classes.find(c => c.id === selectedClass)?.name || 'Selected Class'}`}
            extra={
              <Space>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleExport}
                  disabled={reportData.length === 0}
                >
                  {selectedClass && classSubjectIds && classSubjectIds.length === 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <Tag color="warning">No subjects configured for this class. Configure under Academic Management → Class Subjects.</Tag>
                    </div>
                  )}
                  Export CSV
                </Button>
                {/* Replaced Print button with Download PDF button */}
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleDownloadPDF}
                  disabled={reportData.length === 0}
                >
                  Download PDF
                </Button>
              </Space>
            }
          >
            {loading ? (
              <Spin />
            ) : reportData.length > 0 ? (
              <Table
                columns={generateColumns()}
                dataSource={reportData.map((student, i) => ({
                  ...student,
                  key: student.student_id || i
                }))}
                pagination={{
                  pageSize: pageSize, // Use selected page size
                  showSizeChanger: true, // Enable built-in size changer
                  pageSizeOptions: [10, 20, 50, 100],
                  onShowSizeChange: (current, size) => setPageSize(size),
                  onChange: (page, size) => setPageSize(size)
                }}
                scroll={{ x: 1200 }}
                bordered
              />
            ) : (
              <Empty description="No marks found for this class and exam" />
            )}
          </Card>
        ) : (
          <Empty description="Please select class and exam to view marks" />
        )}
      </Space>
    </div>
  );
};

export default ClassMarkReportPage;
